import React, { useState, useEffect, useCallback, memo } from 'react';
import { fetchServiceTools, toggleTool } from '../../../api/services';
import { getAgentTools, toggleAgentTool } from '../../../api/agents/agent-services';
import ToggleButton from '../../common/button/ToggleButton';

import styles from './styles/ServiceDetails.module.css';

const ServiceTools = ({
    serviceId,
    isAdded,
    isEnabled,
    enableToolSettings,
    isAgentContext = false,
    agentId = null,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [tools, setTools] = useState([]);

    const loadTools = useCallback(async () => {
        try {
            setIsLoading(true);

            if (isAgentContext && agentId) {
                const data = await getAgentTools(agentId, serviceId);
                setTools(data);
            } else {
                const data = await fetchServiceTools(serviceId);
                setTools(data);
            }
        } catch (error) {
            console.error('Error loading tools:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, isAgentContext, agentId]);

    useEffect(() => {
        loadTools();
    }, [loadTools]);

    const handleToggleTool = useCallback(async toolId => {
        setIsLoading(true);
        try {
            if (isAgentContext && agentId) {
                await toggleAgentTool(agentId, serviceId, toolId);
            } else {
                await toggleTool(serviceId, toolId);
            }

            setTools(prev => prev.map(tool =>
                tool.id === toolId
                    ? { ...tool, isEnabled: !tool.isEnabled }
                    : tool
            ));
        } catch (error) {
            console.error('Error toggling tool:', error);
        } finally {
            setIsLoading(false);
        }
    }, [serviceId, isAgentContext, agentId]);

    if (isLoading && tools.length === 0) {
        return <div className={styles.text}>Loading tools...</div>;
    }

    if (!tools || tools.length === 0) {
        return <div className={styles.emptyMessage}>No tools available for this service</div>;
    }

    return (
        <div className={styles.tools}>
            <h3 className={styles.toolsTitle}>Service Tools</h3>
            {tools.map(tool => (
                <div key={tool.id} className={styles.tool}>
                    <div className={styles.toolInfo}>
                        <h4 className={styles.toolName}>{tool.name}</h4>
                        <p className={styles.toolDescription}>{tool.description}</p>
                    </div>
                    {isAdded && (isAgentContext ? true : enableToolSettings) && (
                        <ToggleButton
                            isEnabled={tool.isEnabled}
                            onChange={() => handleToggleTool(tool.id)}
                            size="small"
                            disabled={!isEnabled}
                        />
                    )}
                </div>
            ))}
        </div>
    );
};

export default memo(ServiceTools);
